import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ethers } from 'ethers';
import { locales, Locale } from './locales';
import { contractABI } from './contractABI';

declare global {
  interface Window {
    ethereum?: any;
  }
}

const CONTRACT_ADDRESS = '0xAC98c4B53379BFAD609e1CB9074f3E0053e66773';

const NFTBackground: React.FC = () => {
  return (
    <div className="fixed inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: 'url(/bg.png)' }} />
  );
};

const MintSection: React.FC<{ locale: Locale }> = ({ locale }) => {
  const t = locales[locale];
  const [isConnected, setIsConnected] = useState(false);
  const [account, setAccount] = useState('');
  const [usdcBalance, setUsdcBalance] = useState('0');
  const [ethBalance, setEthBalance] = useState('0');
  const [isMinting, setIsMinting] = useState(false);
  const [totalSupply, setTotalSupply] = useState('0');
  const [maxSupply] = useState('10000');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);

  useEffect(() => {
    checkWalletConnection();
    checkTotalSupply();

    // Set up interval to check supply every 5 seconds
    const interval = setInterval(() => {
      checkTotalSupply();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const checkWalletConnection = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        // Check if we're on the correct network (BASE)
        const chainId = await window.ethereum.request({ method: 'eth_chainId' });
        if (chainId !== '0x2105') { // BASE chain ID
          try {
            await window.ethereum.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: '0x2105' }],
            });
          } catch (switchError: any) {
            // This error code indicates that the chain has not been added to MetaMask
            if (switchError.code === 4902) {
              await window.ethereum.request({
                method: 'wallet_addEthereumChain',
                params: [{
                  chainId: '0x2105',
                  chainName: 'Base',
                  nativeCurrency: {
                    name: 'ETH',
                    symbol: 'ETH',
                    decimals: 18
                  },
                  rpcUrls: ['https://mainnet.base.org'],
                  blockExplorerUrls: ['https://basescan.org']
                }]
              });
            }
          }
        }

        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          setIsConnected(true);
          await checkETHBalance(accounts[0]);
        }
      } catch (error) {
        console.error('Error checking wallet connection:', error);
      }
    }
  };

  const connectWallet = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        // Switch to BASE network
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0x2105' }],
          });
        } catch (switchError: any) {
          // This error code indicates that the chain has not been added to MetaMask
          if (switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: '0x2105',
                chainName: 'Base',
                nativeCurrency: {
                  name: 'ETH',
                  symbol: 'ETH',
                  decimals: 18
                },
                rpcUrls: ['https://mainnet.base.org'],
                blockExplorerUrls: ['https://basescan.org']
              }]
            });
          }
        }

        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        setAccount(accounts[0]);
        setIsConnected(true);
        await checkETHBalance(accounts[0]);
        await checkUSDCBalance(accounts[0]);
        await checkUSDCBalance(accounts[0]);
      } catch (error) {
        console.error('Error connecting wallet:', error);
      }
    } else {
      alert('Please install MetaMask or another Web3 wallet');
    }
  };

  const checkETHBalance = async (userAddress: string) => {
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const balance = await provider.getBalance(userAddress);
      setEthBalance(ethers.utils.formatEther(balance));
    } catch (error) {
      console.error('Error checking ETH balance:', error);
      setEthBalance('0');
    }
  };

  const checkUSDCBalance = async (userAddress: string) => {
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const usdcContract = new ethers.Contract(
        '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913',
        ['function balanceOf(address) view returns (uint256)'],
        provider
      );
      const balance = await usdcContract.balanceOf(userAddress);
      setUsdcBalance(ethers.utils.formatUnits(balance, 6)); // USDC has 6 decimals
    } catch (error) {
      console.error('Error checking USDC balance:', error);
      setUsdcBalance('0');
    }
  };

  const checkTotalSupply = async () => {
    try {
      // Use a public provider instead of MetaMask for read-only calls
      const provider = new ethers.providers.JsonRpcProvider('https://mainnet.base.org');
      const contract = new ethers.Contract(CONTRACT_ADDRESS, contractABI, provider);
      const supply = await contract.totalSupply();
      setTotalSupply(supply.toString());
    } catch (error) {
      console.error('Error checking total supply:', error);
      // Don't show error to user, just keep default value
    }
  };

  // No need for approve with ETH payments

  const mintNFT = async () => {
    if (!isConnected) {
      alert('Please connect your wallet first');
      return;
    }

    setIsMinting(true);
    try {
      // Call x402 validation endpoint to get payment details
      const validationResponse = await fetch('/api/x402');
      const paymentSchema = await validationResponse.json();

      // Show payment modal instead of alert
      setPaymentDetails(paymentSchema.accepts[0]);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Error getting payment details:', error);
      alert('Error initializing payment process');
      setIsMinting(false);
    }
  };

  const confirmPayment = async () => {
    setShowPaymentModal(false);
    try {
      // In real x402 implementation, the payment would be handled by x402 protocol
      // For now, we'll simulate the payment process by transferring USDC directly
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();

      // USDC contract on BASE
      const usdcContract = new ethers.Contract(
        '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913',
        [
          'function transfer(address to, uint256 amount) returns (bool)',
          'function approve(address spender, uint256 amount) returns (bool)',
          'function balanceOf(address) view returns (uint256)'
        ],
        signer
      );

      // Check USDC balance
      const balance = await usdcContract.balanceOf(account);
      if (balance.lt(ethers.utils.parseUnits('1', 6))) {
        alert('Insufficient USDC balance. You need at least 1 USDC to mint.');
        setIsMinting(false);
        return;
      }

      // Transfer 1 USDC to contract
      const tx = await usdcContract.transfer(CONTRACT_ADDRESS, ethers.utils.parseUnits('1', 6));
      console.log('Payment transaction sent:', tx.hash);

      // Wait for confirmation
      const receipt = await tx.wait();
      console.log('Payment confirmed:', receipt.transactionHash);

      // Use the payment tx hash for minting
      const paymentTxHash = receipt.transactionHash;

      // After payment, call mint endpoint
      const mintResponse = await fetch('/api/mint', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient: account,
          txHash: paymentTxHash
        })
      });

      const mintResult = await mintResponse.json();

      if (mintResult.success) {
        alert(`🎉 NFT Minted Successfully!\n\nToken ID: ${mintResult.tokenId}\nMint Transaction: ${mintResult.transactionHash}\nPayment Transaction: ${paymentTxHash}`);
        await checkTotalSupply(); // Update supply
        await checkUSDCBalance(account); // Update balance
      } else {
        console.error('Minting failed:', mintResult.error);
        alert(`Minting failed: ${mintResult.error}\nCheck console for full error details`);
      }
    } catch (error) {
      console.error('Minting error:', error);
      alert('Error during minting process');
    } finally {
      setIsMinting(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="relative z-10 bg-black bg-opacity-85 rounded-3xl p-8 max-w-2xl w-full mx-4"
      >
        {/* Paid Minting Section */}
        <div>
          <h2 className="text-3xl font-bold text-center mb-6 text-[#2196F3] drop-shadow-lg">
            Mint x402mfers NFT
          </h2>
          <div className="text-center mb-4">
            <p className="text-white text-lg">
              Minted: {totalSupply} / {maxSupply}
            </p>
          </div>
          <div className="text-center space-y-4">
            {!isConnected ? (
              <button
                className="bg-[#F5FF00] text-black px-8 py-3 rounded-full font-bold text-lg hover:bg-[#AFFF00] transition-all duration-300 shadow-lg hover:shadow-[#F5FF00] hover:shadow-2xl transform hover:scale-105"
                onClick={connectWallet}
              >
                {t.connectWallet}
              </button>
            ) : (
              <div className="space-y-4">
                <div className="text-white text-sm">
                  Connected: {account.slice(0, 6)}...{account.slice(-4)}
                </div>
                <div className="text-white text-sm">
                  ETH Balance: {usdcBalance}
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    className="bg-[#2196F3] text-white px-8 py-3 rounded-full font-bold text-lg hover:bg-[#1976D2] transition-all duration-300 shadow-lg hover:shadow-[#2196F3] hover:shadow-2xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={mintNFT}
                    disabled={isMinting}
                  >
                    {isMinting ? 'Processing...' : 'Mint NFT via x402 (1 USDC)'}
                  </button>
                  <button
                    className="bg-[#F5FF00] text-black px-8 py-3 rounded-full font-bold text-lg hover:bg-[#AFFF00] transition-all duration-300 shadow-lg hover:shadow-[#F5FF00] hover:shadow-2xl transform hover:scale-105"
                    onClick={async () => {
                      try {
                        const response = await fetch('/api/x402');
                        const data = await response.json();
                        console.log('x402 Response:', data);
                        alert(`x402 Integration Test:\nStatus: ${response.status}\nCheck console for full response`);
                      } catch (error) {
                        console.error('x402 test error:', error);
                        alert('x402 test failed - check console');
                      }
                    }}
                  >
                    Test x402
                  </button>
                </div>
              </div>
            )}
            <p className="text-white text-sm">Mint your x402mfers NFT now!</p>
          </div>
        </div>
      </motion.div>

      {/* Payment Modal */}
      {showPaymentModal && paymentDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-black bg-opacity-95 rounded-3xl p-8 max-w-md w-full mx-4 border-2 border-[#F5FF00]"
          >
            <h3 className="text-2xl font-bold text-center mb-6 text-[#F5FF00]">
              x402 Payment Required
            </h3>
            <div className="space-y-4 mb-6">
              <div className="flex justify-between">
                <span className="text-white">Network:</span>
                <span className="text-[#AFFF00] font-bold">{paymentDetails.network.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Amount:</span>
                <span className="text-[#AFFF00] font-bold">1 USDC</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Asset:</span>
                <span className="text-[#AFFF00] font-bold text-xs">{paymentDetails.asset.slice(0, 6)}...{paymentDetails.asset.slice(-4)}</span>
              </div>
            </div>
            <div className="text-center text-white text-sm mb-6">
              Click "Confirm Payment" to transfer 1 USDC and mint your NFT.
            </div>
            <div className="flex gap-4">
              <button
                className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-full hover:bg-gray-500 transition-all"
                onClick={() => {
                  setShowPaymentModal(false);
                  setIsMinting(false);
                }}
              >
                Cancel
              </button>
              <button
                className="flex-1 bg-[#F5FF00] text-black px-4 py-2 rounded-full font-bold hover:bg-[#AFFF00] transition-all"
                onClick={confirmPayment}
              >
                Confirm Payment
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

const Footer: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.5, duration: 1 }}
      className="relative z-10 mt-8 flex justify-center space-x-6"
    >
      <a
        href="https://x.com"
        target="_blank"
        rel="noopener noreferrer"
        className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center hover:bg-[#F5FF00] transition-all duration-300 overflow-hidden"
      >
        <img src="/images/twitter.png" alt="Twitter" className="w-6 h-6" />
      </a>
      <a
        href="https://opensea.io"
        target="_blank"
        rel="noopener noreferrer"
        className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center hover:bg-[#F5FF00] transition-all duration-300 overflow-hidden"
      >
        <img src="/images/opensea.png" alt="OpenSea" className="w-8 h-8" />
      </a>
    </motion.div>
  );
};

function App() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black overflow-hidden">
      <NFTBackground />
      <MintSection locale="en" />
      <Footer />
    </div>
  );
}

export default App;
